// Who a task can go to, as the worker dropdown lists them: the agents, the
// subagents, the Team when jobs are on, and the saved workflows when
// workflows are on. Used by the one start dialog (features/startwork) and
// the old Schedules page's task schedule dialog. `active` holds the reads
// until the dialog opens. A draft subagent (not published yet) and the
// Team's own members are left out: neither can be given work. Every
// subagent is listed while the drafts are still being read. A role that
// only repeats the name ("Ray Dalio / Ray Dalio"), or a subagent's
// template it is named from ("Ray-Dalio"), is no hint and is left out; an
// agent's template is never shown, so its role ("Operations") stays.
// `limitOf` is the most a task of that worker may run, as the server
// counts it (a subagent's own limit under the workspace cap; an agent the
// cap): null while unknown.

import { useCustomerSettings } from "@/api/customerSettings";
import { isDraftSubagent } from "@/api/subagentDesigns";
import { useSubagentBlueprints } from "@/api/subagentBlueprints";
import { useWorkflows } from "@/api/workflows";
import type { DropdownOption } from "@/components/ui/Dropdown";
import { JOB_TEAM_TEMPLATES } from "@/features/subagents/purpose";
import { useAgentDirectory } from "@/hooks/useAgentDirectory";

const plain = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");

/** The role as a hint, unless it says nothing the name (or, for a subagent, its template) does not. */
function hintOf(a: { role?: string | null; name: string; template: string }, byTemplate: boolean): string | undefined {
  const role = a.role?.trim();
  if (!role) return undefined;
  const p = plain(role);
  return p === plain(a.name) || (byTemplate && p === plain(a.template)) ? undefined : role;
}

export function useWorkerOptions(customerId: string, active: boolean) {
  const settings = useCustomerSettings(customerId || null).data;
  const workflowsOn = settings?.workflows_enabled === true && settings?.subagents_enabled === true;
  const teamOn = workflowsOn && settings?.jobs_enabled === true;
  const workflows = useWorkflows(workflowsOn && active ? customerId : null);
  const dir = useAgentDirectory(customerId);
  const subagents = dir.agents.filter((a) => a.kind === "subagent");
  const blueprints = useSubagentBlueprints(active && subagents.length > 0 ? customerId : null);
  const drafts = new Set((blueprints.data?.blueprints ?? []).filter(isDraftSubagent).map((b) => b.template));
  const isSubagent = (t: string) => subagents.some((a) => a.template === t);
  const cap = blueprints.data?.caps.run_timeout_minutes ?? null;
  const limitOf = (t: string): number | null => {
    if (cap === null || !t) return null;
    if (!isSubagent(t)) return cap;
    const found = blueprints.data?.blueprints.find((b) => b.template === t);
    return found ? Math.min(found.subagent.limits?.run_timeout_minutes ?? 240, cap) : null;
  };
  const givable = subagents.filter((a) => !drafts.has(a.template) && !JOB_TEAM_TEMPLATES.has(a.template));
  const options: DropdownOption<string>[] = [
    ...dir.agents.filter((a) => a.kind === "standard").map((a) => ({ value: a.template, label: a.name, hint: hintOf(a, false), group: "Agents" })),
    ...givable.map((a) => ({ value: a.template, label: a.name, hint: hintOf(a, true), group: "Subagents" })),
    ...(teamOn ? [{ value: "team", label: "Team", group: "Team" }] : []),
    ...(workflows.data?.workflows ?? []).map((w) => ({ value: `workflow:${w.slug}`, label: w.name || w.slug, group: "Workflows" })),
  ];
  return { options, isSubagent, limitOf };
}
